// Page-context only — never import from src/sw/** or src/lib/sw.ts (see aiImportBoundary.test.ts).

import type { AIProvider } from "@reflecto/client-core/ai";
import {
  type GlanceLabels,
  Glance as HeadlessGlance,
  type UseAIStatusResult,
} from "@reflecto/client-ui";
import { useCallback, useMemo, useRef } from "react";
import { webStorage } from "../adapters/storage.web";
import { useStorage } from "../hooks/useStorage";
import { messages } from "../i18n";
import { trackGlanceDownloadConsented } from "../services/analytics";

// Web DI seam over the headless `Glance`: a read-only, header-triggered AI recap. Wires the
// injected AI status + data loaders; the trailing-slot button + `useAIStatus` live in MainPage
// (so the gate + the card share one probe). No reply/dismiss/mark-read DI — the recap is read-only.

const LABELS: GlanceLabels = {
  title: messages.glance.title,
  appName: messages.glance.appName,
  closeLabel: messages.glance.closeLabel,
  aiSummaryTag: messages.glance.aiSummaryTag,
  errorAi: messages.glance.errorAi,
  retryCta: messages.glance.retryCta,
  downloadHeading: messages.glance.downloadHeading,
  downloadBody: messages.glance.downloadBody,
  downloadCta: messages.glance.downloadCta,
  downloadPrivacyNote: messages.glance.downloadPrivacyNote,
  downloadingHeading: messages.glance.downloadingHeading,
  caughtUp: messages.glance.caughtUp,
  needsYouLabel: messages.glance.needsYou,
  quietLabel: messages.glance.quiet,
  scanningLabel: messages.glance.scanning,
  jumpLabel: messages.glance.jump,
  affordanceReply: messages.glance.affordanceReply,
  affordanceOpen: messages.glance.affordanceOpen,
  affordanceCopyOtp: messages.glance.affordanceCopyOtp,
};

// navigator.language is fixed per session, so resolve the summary language once — same rationale as
// LABELS. Chrome's Prompt API narrates en/es/ja; every other UI locale (e.g. de) falls back to en,
// since the on-device model can't produce it.
const WEB_LANGUAGE = navigator.language.toLowerCase();
const OUTPUT_LANGUAGE = WEB_LANGUAGE.startsWith("es")
  ? "es"
  : WEB_LANGUAGE.startsWith("ja")
    ? "ja"
    : "en";

// Stable empty-cache ref so an unhydrated appIconCache doesn't pass a fresh `{}` each render.
const EMPTY_ICON_CACHE: Record<string, string> = {};

export function Glance({
  publicMode,
  provider,
  aiStatus,
  trigger,
  onJumpTo,
}: {
  publicMode: boolean;
  // Injected from MainPage so the trailing-slot gate + this card share a single AI probe.
  provider: AIProvider;
  aiStatus: UseAIStatusResult;
  // Header-button counter; bumping it surfaces the card (the digest is pre-computed ambiently, not run on bump).
  trigger: number;
  // When provided, the headless card renders `›` jump links and calls this on click.
  onJumpTo?: (target: { msgId: string } | { conversationKey: string }) => void;
}) {
  const { value: notifications } = useStorage("notifications");
  const { value: conversations } = useStorage("conversations");
  const { value: hiddenApps } = useStorage("hiddenApps");
  const { value: dndSchedule } = useStorage("dndSchedule");
  const { value: appIconCache } = useStorage("appIconCache");

  // Loaders read the latest storage snapshot via refs so their identity stays stable across writes.
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  const loadNotifications = useCallback(async () => notificationsRef.current ?? [], []);
  const loadConversations = useCallback(
    async () => Object.values(conversationsRef.current ?? {}),
    []
  );

  const prefs = useMemo(
    () => ({
      hiddenApps: hiddenApps ?? [],
      dndBypassApps: dndSchedule?.bypassApps,
      publicMode,
    }),
    [hiddenApps, dndSchedule, publicMode]
  );

  // Re-trigger the ambient triage pass when our data changes — including the async idb hydration on a cold
  // load, which would otherwise let the driver's first pass run on empty loaders and persist an empty
  // "caught up" digest that never refreshes (data was READ, not written, so no onChanged fires). Counts are
  // enough: add / remove / hydration all change them; live in-place content edits still arrive via writes.
  const dataVersion = `${notifications?.length ?? 0}:${Object.keys(conversations ?? {}).length}`;

  // Stable identity so HeadlessGlance doesn't re-render on every parent update mid-download.
  const downloadProgress = useMemo(
    () => ({ loaded: aiStatus.progressLoaded ?? 0, total: aiStatus.progressTotal }),
    [aiStatus.progressLoaded, aiStatus.progressTotal]
  );

  // Download CTA = on-device-AI consent gesture (§4.1).
  const handleDownload = useCallback(() => {
    trackGlanceDownloadConsented();
    return aiStatus.onDownload();
  }, [aiStatus.onDownload]);

  return (
    <HeadlessGlance
      adapter={webStorage}
      provider={provider}
      availability={aiStatus.availability}
      dataVersion={dataVersion}
      outputLanguage={OUTPUT_LANGUAGE}
      loadNotifications={loadNotifications}
      loadConversations={loadConversations}
      prefs={prefs}
      startDownload={handleDownload}
      downloadError={aiStatus.errorMessage}
      downloadProgress={downloadProgress}
      labels={LABELS}
      appIconCache={appIconCache ?? EMPTY_ICON_CACHE}
      trigger={trigger}
      onJumpTo={onJumpTo}
    />
  );
}
